import { GAP_MOCK_DEFAULTS as d } from '../data/methodologyConstants';
import { clamp } from './interpolate';

/** F05 bits 6–8 (1-indexed) → 0-based indices 5,6,7 — Margin / Agency / E-com. */
const F05_RISKY_BIT_INDICES = [5, 6, 7];

const sumNet = (rows) => rows.reduce((s, i) => s + (Number(i.F30) || 0), 0);

const monthKey = (dateStr) => dateStr?.slice(0, 7);

const hasRiskyChannelBit = (f05) => {
  const flag = String(f05 || '');
  return F05_RISKY_BIT_INDICES.some((idx) => flag[idx] === '1');
};

export const aggregateFromInvoices = (invoices, gapMocks) => {
  const sales = (invoices || []).filter((i) => i.F03 === '380');
  const creditNotes = (invoices || []).filter((i) => i.F03 === '381');
  const ttmRevenue = sumNet(sales);

  const monthlyTotals = {};
  sales.forEach((inv) => {
    const key = monthKey(inv.F02);
    if (!key) return;
    monthlyTotals[key] = (monthlyTotals[key] || 0) + (Number(inv.F30) || 0);
  });
  const monthlyValues = Object.values(monthlyTotals);
  const monthlyMean = monthlyValues.length > 0 ? monthlyValues.reduce((a, b) => a + b, 0) / monthlyValues.length : 0;
  const monthlyStdev =
    monthlyValues.length > 1
      ? Math.sqrt(monthlyValues.reduce((s, v) => s + (v - monthlyMean) ** 2, 0) / (monthlyValues.length - 1))
      : 0;

  const buyerRevenue = {};
  const buyerCounts = {};
  sales.forEach((inv) => {
    const buyerId = inv.F24;
    if (!buyerId) return;
    buyerRevenue[buyerId] = (buyerRevenue[buyerId] || 0) + (Number(inv.F30) || 0);
    buyerCounts[buyerId] = (buyerCounts[buyerId] || 0) + 1;
  });
  const activeBuyers = Object.keys(buyerRevenue).length;
  const recurringBuyers = Object.values(buyerCounts).filter((c) => c >= 3).length;

  let hhi = 0;
  let top5Share = 0;
  if (ttmRevenue > 0) {
    const shares = Object.values(buyerRevenue).map((v) => v / ttmRevenue);
    hhi = shares.reduce((s, sh) => s + sh * sh, 0);
    top5Share = [...shares]
      .sort((a, b) => b - a)
      .slice(0, 5)
      .reduce((s, v) => s + v, 0);
  }

  const weightedDso =
    ttmRevenue > 0 ? sales.reduce((s, i) => s + (Number(i.F09) || 0) * (Number(i.F30) || 0), 0) / ttmRevenue : 0;
  const paymentMeans = new Set(sales.map((i) => String(i.F07)).filter(Boolean)).size;

  const bmShare = (predicate) => {
    if (ttmRevenue <= 0) return 0;
    return sumNet(sales.filter(predicate)) / ttmRevenue;
  };

  const standardB2bShare = bmShare((i) => String(i.F05).startsWith('101'));
  const freeZoneShare = bmShare((i) => String(i.F05)[3] === '1');
  const exportShareBm = bmShare((i) => String(i.F05)[4] === '1');
  const riskyChannelShare = bmShare((i) => hasRiskyChannelBit(i.F05));

  const aedShare = bmShare((i) => i.F04 === 'AED');
  const currencies = new Set(sales.map((i) => i.F04).filter(Boolean)).size;
  const mainlandConcentration = bmShare((i) => i.F29 === 'N' && i.F20 === 'AE');
  const exportRevenueShare = bmShare((i) => i.F20 && i.F20 !== 'AE');

  const taxShare = (cat) => bmShare((i) => i.F35 === cat);

  const ftaValidatedCount = Math.round(sales.length * (gapMocks.ftaValidatedRatio ?? 1));

  return {
    D1: {
      ttmRevenue,
      prior12MRevenue: gapMocks.prior12MRevenue,
      monthlyMean,
      monthlyStdev,
      ftaValidatedCount,
      totalInvoiceCount: sales.length,
    },
    D2: {
      weightedDso,
      sectorBenchmarkDso: gapMocks.sectorBenchmarkDso,
      collectionRate: gapMocks.collectionRate,
      distinctPaymentMeans: paymentMeans,
    },
    D3: {
      hhi,
      top5Share,
      activeBuyers,
      recurringBuyers,
    },
    D4: {
      distinctCategories: gapMocks.distinctCategories,
      avgLinesPerInvoice: gapMocks.avgLinesPerInvoice,
      unitPriceCv: gapMocks.unitPriceCv,
    },
    D5: {
      standardB2bShare,
      freeZoneShare,
      exportShare: exportShareBm,
      riskyChannelShare,
    },
    D6: {
      aedShare,
      distinctCurrencies: currencies,
      mainlandConcentration,
      exportRevenueShare,
    },
    D7: {
      creditNoteRatio: sales.length > 0 ? creditNotes.length / sales.length : 0,
      sectorDisputeRate: gapMocks.sectorDisputeRate,
      avgDisputeResolutionDays: gapMocks.avgDisputeResolutionDays,
      litigatedDisputes: gapMocks.litigatedDisputes,
    },
    D8: {
      standardRatedShare: taxShare('S'),
      zeroRatedShare: taxShare('Z'),
      exemptShare: taxShare('E'),
      reverseChargeAnomalies: gapMocks.reverseChargeAnomalies,
      filingConsistency: gapMocks.filingConsistency,
    },
    summary: {
      ttmRevenue,
      invoiceCount: sales.length,
      activeBuyers,
    },
  };
};

/** Seeded PRNG + wider gap-mock jitter for demo score/dimension spread. */
export const createGapMocks = (seed) => {
  let s = seed >>> 0;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  const jitter = (value, minRatio = 0.7, maxRatio = 1.35) => {
    const ratio = minRatio + rand() * (maxRatio - minRatio);
    return value * ratio;
  };

  return {
    prior12MRevenue: jitter(d.prior12MRevenue, 0.55, 1.55),
    ftaValidatedRatio: clamp(jitter(d.ftaValidatedRatio, 0.85, 1.0), 0.8, 1),
    sectorBenchmarkDso: jitter(d.sectorBenchmarkDso, 0.65, 1.45),
    collectionRate: clamp(jitter(d.collectionRate, 0.85, 1.12), 70, 100),
    distinctCategories: Math.round(clamp(jitter(d.distinctCategories, 0.6, 1.5), 6, 20)),
    avgLinesPerInvoice: clamp(jitter(d.avgLinesPerInvoice, 0.65, 1.5), 1.5, 10),
    unitPriceCv: clamp(jitter(d.unitPriceCv, 0.5, 1.8), 0.02, 0.2),
    sectorDisputeRate: clamp(jitter(d.sectorDisputeRate, 0.45, 1.8), 0.01, 0.08),
    avgDisputeResolutionDays: Math.round(clamp(jitter(d.avgDisputeResolutionDays, 0.5, 1.7), 5, 30)),
    litigatedDisputes: d.litigatedDisputes,
    reverseChargeAnomalies: d.reverseChargeAnomalies,
    filingConsistency: clamp(jitter(d.filingConsistency, 0.92, 1.0), 88, 100),
  };
};

export { hasRiskyChannelBit, F05_RISKY_BIT_INDICES };
